import React, { useEffect, useState } from "react";
import CourseCard from "./CourseCard";
import "../../assets/stylesheets/CourseList.css";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SortIcon from "@mui/icons-material/Sort";
import { fetchData } from "../../api/api";

const CourseList = ({
  courses,
  onEdit,
  onPublish,
  onDelete,
  onSelect,
  adminStatus,
}) => {
  const [categories, setCategories] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  // Per-subcategory sort: { [subcategoryId]: "name"|"name_desc"|"date"|"date_desc" }
  const [subcategorySorts, setSubcategorySorts] = useState({});
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [sortMenuId, setSortMenuId] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await fetchData("categories");
        setCategories(categories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const toggleCollapse = (id, type = "cat") => {
    const key = `${type}_${id}`;
    setCollapsedCategories((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const coursesByCategory = courses.reduce((acc, course) => {
    const { category_id, subcategory_id } = course;
    if (!acc[category_id]) acc[category_id] = {};
    if (subcategory_id) {
      if (!acc[category_id][subcategory_id])
        acc[category_id][subcategory_id] = [];
      acc[category_id][subcategory_id].push(course);
    } else {
      if (!acc[category_id].main) acc[category_id].main = [];
      acc[category_id].main.push(course);
    }
    return acc;
  }, {});

  const getCategoryDetails = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category
      ? category
      : { name: "Uncategorized", bg_color: "#f5f5f5", font_color: "#000" };
  };

  const getSubcategoryDetails = (categoryId, subcategoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    const subcategory = category
      ? category.subcategories.find((subcat) => subcat.id === subcategoryId)
      : null;
    return subcategory
      ? subcategory
      : { name: "No Subcategory", bg_color: "#e0e0e0", font_color: "#000" };
  };

  const openSortMenu = (e, subcategoryId) => {
    e.stopPropagation();
    setSortMenuAnchor(e.currentTarget);
    setSortMenuId(subcategoryId);
  };

  const closeSortMenu = () => {
    setSortMenuAnchor(null);
    setSortMenuId(null);
  };

  const handleSubcategorySort = (sort) => {
    setSubcategorySorts((prev) => ({ ...prev, [sortMenuId]: sort }));
    closeSortMenu();
  };

  const sortCourses = (courseArr, sort) => {
    if (!sort) return courseArr;
    return [...courseArr].sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "name_desc") return b.name.localeCompare(a.name);
      if (sort === "date") return new Date(a.updated_at) - new Date(b.updated_at);
      if (sort === "date_desc") return new Date(b.updated_at) - new Date(a.updated_at);
      return 0;
    });
  };

  if (loading) {
    return <CircularProgress />;
  }

  const sortedCategories = Object.entries(coursesByCategory).sort(
    ([a], [b]) => {
      if (a === "11") return -1;
      if (b === "11") return 1;
      const firstCourseA = courses.find(
        (course) => course.category_id === parseInt(a, 10)
      );
      const firstCourseB = courses.find(
        (course) => course.category_id === parseInt(b, 10)
      );
      return courses.indexOf(firstCourseA) - courses.indexOf(firstCourseB);
    }
  );

  return (
    <div className="course-list">
      {sortedCategories.map(([categoryId, subcategories]) => {
        const categoryDetails = getCategoryDetails(parseInt(categoryId, 10));
        return (
          <Accordion
            key={categoryId}
            expanded={!!collapsedCategories[`cat_${categoryId}`]}
            onChange={() => toggleCollapse(categoryId, "cat")}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${categoryId}-content`}
              id={`${categoryId}-header`}
              sx={{
                backgroundColor: categoryDetails.bg_color,
                color: categoryDetails.font_color,
                borderBottom: "1px solid #ddd",
                maxWidth: "100%",
              }}
            >
              <Typography variant="h6">{categoryDetails.name}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ overflow: "auto" }}>
              <div className="courses-container">
                {subcategories.main &&
                  subcategories.main.map((course) => (
                    <CourseCard
                      key={course.id}
                      {...course}
                      onEdit={(event) => onEdit(course.id, event)}
                      onPublish={(event) => onPublish(course.id, event)}
                      onDelete={(event) => onDelete(course.id, event)}
                      onSelect={(event) => onSelect(course.id, event)}
                      adminStatus={adminStatus}
                    />
                  ))}
              </div>
              {Object.entries(subcategories)
                .filter(([key]) => key !== "main")
                .sort(([aId], [bId]) => {
                  const subA = getCategoryDetails(parseInt(categoryId, 10))
                    ?.subcategories?.find((s) => s.id === parseInt(aId, 10));
                  const subB = getCategoryDetails(parseInt(categoryId, 10))
                    ?.subcategories?.find((s) => s.id === parseInt(bId, 10));
                  return (subA?.sort_order ?? 0) - (subB?.sort_order ?? 0);
                })
                .map(([subcategoryId, subcategoryCourses]) => {
                  const subcategoryDetails = getSubcategoryDetails(
                    parseInt(categoryId, 10),
                    parseInt(subcategoryId, 10)
                  );
                  return (
                    <Accordion
                      key={subcategoryId}
                      expanded={!!collapsedCategories[`sub_${subcategoryId}`]}
                      onChange={() => toggleCollapse(subcategoryId, "sub")}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${subcategoryId}-content`}
                        id={`${subcategoryId}-header`}
                        sx={{
                          backgroundColor: subcategoryDetails.bg_color,
                          color: subcategoryDetails.font_color,
                          borderBottom: "1px solid #ddd",
                          maxWidth: "100%",
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ flexGrow: 1, alignSelf: "center" }}>
                          {subcategoryDetails.name}
                        </Typography>
                        <IconButton
                          size="small"
                          title="Sortierung"
                          onClick={(e) => openSortMenu(e, subcategoryId)}
                          sx={{ color: subcategoryDetails.font_color, mr: 1 }}
                        >
                          <SortIcon fontSize="small" />
                        </IconButton>
                      </AccordionSummary>
                      <AccordionDetails sx={{ overflow: "auto" }}>
                        <div className="courses-container">
                          {sortCourses(subcategoryCourses, subcategorySorts[subcategoryId]).map((course) => (
                            <CourseCard
                              key={course.id}
                              {...course}
                              onEdit={(event) => onEdit(course.id, event)}
                              onPublish={(event) => onPublish(course.id, event)}
                              onDelete={(event) => onDelete(course.id, event)}
                              onSelect={(event) => onSelect(course.id, event)}
                              adminStatus={adminStatus}
                            />
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </AccordionDetails>
          </Accordion>
        );
      })}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={closeSortMenu}
      >
        <MenuItem onClick={() => handleSubcategorySort("name")}>Name A-Z</MenuItem>
        <MenuItem onClick={() => handleSubcategorySort("name_desc")}>Name Z-A</MenuItem>
        <MenuItem onClick={() => handleSubcategorySort("date")}>Datum aufsteigend</MenuItem>
        <MenuItem onClick={() => handleSubcategorySort("date_desc")}>Datum absteigend</MenuItem>
        <MenuItem onClick={() => handleSubcategorySort(null)}>Standard</MenuItem>
      </Menu>
    </div>
  );
};

export default CourseList;
